import React, { useState, useEffect, useRef } from "react";
import { ListPageSkeleton } from "../../Components/Skeleton";
import SearchBar from "../../Components/SearchBar";
import Button from "../../Components/Button";
import { IoMdAdd } from "react-icons/io";
import { Modal } from "../../Components/Modal";
import { supabase } from "../../supabaseClient";
import { MdModeEditOutline, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function Brand() {
  const [showModal, setShowModal] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editBrand, setEditBrand] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  const fetchBrands = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.from("Brand").select("*");
    if (error) {
      console.error("Error fetching brands:", error);
      setBrands([]);
    } else {
      setBrands(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleDeleteBrand = async (id) => {
    try {
      const { error } = await supabase.from("Brand").delete().eq("id", id);
      if (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Failed to delete brand",
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          background: "white",
          customClass: { popup: "shadow-lg rounded-md p-3" },
        });
      } else {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Brand deleted successfully",
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          background: "white",
          customClass: { popup: "shadow-lg rounded-md p-3" },
        });
        fetchBrands();
      }
    } catch (err) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        showConfirmButton: false,
        timer: 3000,
        toast: true,
        background: "white",
        customClass: { popup: "shadow-lg rounded-md p-3" },
      });
    }
  };

  useEffect(() => {
    if (showModal && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showModal]);

  // Debounce hook
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  // Filter on search
  useEffect(() => {
    const fetchFilteredBrands = async () => {
      setIsLoading(true);

      let query = supabase.from("Brand").select("*");

      if (debouncedSearch.trim() !== "") {
        query = query.ilike("name", `%${debouncedSearch}%`);
      }

      const { data, error } = await query;
      if (error) {
        setBrands([]);
      } else {
        setBrands(data);
      }
      setIsLoading(false);
    };

    fetchFilteredBrands();
  }, [debouncedSearch]);

  return (
    <div className="h-screen p-6 bg-white">
      {/* Top Controls */}
      <div className="flex flex-wrap justify-center items-center mb-4 gap-4">
        <div className="w-full sm:w-3/4 md:w-2/3 lg:w-1/3">
          <SearchBar
            placeholder="Search Brand"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputClassName="py-3"
          />
        </div>

        <Button
          variant="primary"
          onClick={() => navigate("/brand/create")}
          className="px-4 text-sm flex justify-between items-center gap-3"
        >
          <IoMdAdd />
          Add Brand
        </Button>
      </div>

      {/* Brand List */}
      <div className="w-full mx-auto border p-4 h-[calc(100vh-7rem)] overflow-auto rounded-lg bg-white">
        <h1 className="text-3xl font-semibold uppercase font-serif border-b pb-3 mb-4">
          Brand
        </h1>

        {isLoading ? (
          <ListPageSkeleton count={8} />
        ) : brands.length === 0 ? (
          <p className="text-center font-semibold text-red-500">
            No data found
          </p>
        ) : (
          brands.map((item) => (
            <div
              key={item.id}
              onClick={(e) => {
                const isButton = e.target.closest("button");
                if (!isButton)
                  navigate(`/tile?brand=${encodeURIComponent(item.id)}`);
              }}
              className="mb-3 bg-white px-4 py-2 rounded-lg transition duration-500 transform border border-gray-400 hover:shadow-lg"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-md font-semibold text-gray-800 truncate">
                  {item.name}
                </h3>

                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(`/brand/${item.id}`)}
                    className="p-2 bg-yellow-100 text-yellow-500 rounded-full hover:bg-yellow-200 transition"
                  >
                    <MdModeEditOutline />
                  </button>

                  <button
                    onClick={() => handleDeleteBrand(item.id)}
                    className="p-2 bg-red-100 text-red-500 rounded-full hover:bg-red-200 transition"
                  >
                    <MdDelete />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        customClasses="w-full sm:max-w-lg md:max-w-2xl"
        showModal={showModal}
        setShowModal={setShowModal}
      >
        <div className="space-y-4 border-slate-300 border bg-gray-100 p-4 mx-auto rounded-md relative">
          <h2 className="text-xl font-semibold mb-4">
            {editBrand ? "Edit Brand" : "Add Brand"}
          </h2>

          <input
            type="text"
            placeholder="Enter brand name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
            ref={inputRef}
          />

          <div className="flex justify-end gap-2">
            
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Brand;
